import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DictateButton from "@/components/DictateButton";
import { Scissors, Truck } from "lucide-react";

const INHOUSE_TYPES = ["Recut", "Levels", "H&E", "Unstained", "IHC", "Special Stain", "Frozen", "FNA"];

const EMPTY_INHOUSE = { block_id: "", request_type: "Recut", levels: 1, priority: "Routine", note: "" };
const EMPTY_SENDOUT = {
  destination_lab: "", test_requested: "", reason: "", priority: "Routine",
  specimen_ids: [], notes: "",
};

// One place for a pathologist to order in-house histology work or a send-out.
export default function RequestDialog({ open, onOpenChange, caseId, cases = [], onDone,
                                        canOrderInhouse = true, canOrderSendout = true }) {
  const [tab, setTab] = useState(canOrderInhouse ? "inhouse" : "sendout");
  const [selectedCase, setSelectedCase] = useState(caseId || "");
  const [specimens, setSpecimens] = useState([]);
  const [inhouse, setInhouse] = useState(EMPTY_INHOUSE);
  const [sendout, setSendout] = useState(EMPTY_SENDOUT);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setSelectedCase(caseId || ""); }, [open, caseId]);

  useEffect(() => {
    if (!open || !selectedCase) { setSpecimens([]); return; }
    api.get(`/cases/${selectedCase}`)
      .then((r) => setSpecimens(r.data.specimens.filter((s) => !s.voided)))
      .catch(() => setSpecimens([]));
  }, [open, selectedCase]);

  const blocks = specimens.filter((s) => s.kind === "block");
  const slides = specimens.filter((s) => s.kind === "slide");

  const toggleMaterial = (id) => setSendout((f) => ({
    ...f,
    specimen_ids: f.specimen_ids.includes(id)
      ? f.specimen_ids.filter((x) => x !== id)
      : [...f.specimen_ids, id],
  }));

  const reset = () => { setInhouse(EMPTY_INHOUSE); setSendout(EMPTY_SENDOUT); };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedCase) return toast.error("Pick a case first");
    setBusy(true);
    try {
      if (tab === "inhouse") {
        if (!inhouse.block_id) { setBusy(false); return toast.error("Select the block to work from"); }
        await api.post("/recuts", { ...inhouse, case_id: selectedCase, levels: Number(inhouse.levels) || 1 });
        toast.success("Sent to histology");
      } else {
        if (!sendout.test_requested.trim()) { setBusy(false); return toast.error("What test or consultation is being requested?"); }
        if (sendout.specimen_ids.length === 0) { setBusy(false); return toast.error("Tick the blocks and/or slides that go out"); }
        await api.post("/sendouts", { ...sendout, case_id: selectedCase, sendout_status: "Requested" });
        toast.success("Send-out requested — histology will arrange the courier");
      }
      reset();
      onOpenChange(false);
      onDone?.();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const tabBtn = (key, label, Icon) => (
    <button type="button" onClick={() => setTab(key)} data-testid={`request-tab-${key}`}
            className={`flex flex-1 items-center justify-center gap-2 border px-3 py-2.5 text-sm font-semibold transition-colors ${
              tab === key ? "border-[#134e4a] bg-[#134e4a] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[#134e4a]"}`}>
      <Icon size={15} /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="histology-request-dialog">
        <DialogHeader><DialogTitle className="font-display">New histology request</DialogTitle></DialogHeader>

        <div className="flex gap-2">
          {canOrderInhouse && tabBtn("inhouse", "In-house work", Scissors)}
          {canOrderSendout && tabBtn("sendout", "Send-out", Truck)}
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="histology-request-form">
          {!caseId && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Case</Label>
              <select value={selectedCase} onChange={(e) => setSelectedCase(e.target.value)}
                      data-testid="request-case-select"
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                <option value="">Select case…</option>
                {cases.map((c) => (
                  <option key={c.id} value={c.id}>{`${c.accession_number} — ${c.patient_name}`}</option>
                ))}
              </select>
            </div>
          )}

          {tab === "inhouse" ? (
            <>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Block</Label>
                <select value={inhouse.block_id} onChange={(e) => setInhouse((f) => ({ ...f, block_id: e.target.value }))}
                        data-testid="request-block-select"
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  <option value="">Select block…</option>
                  {blocks.map((b) => <option key={b.id} value={b.id}>{`Block ${b.block_designation || b.label} — ${b.barcode}`}</option>)}
                </select>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Request</Label>
                  <select value={inhouse.request_type} onChange={(e) => setInhouse((f) => ({ ...f, request_type: e.target.value }))}
                          data-testid="request-type-select"
                          className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                    {INHOUSE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Levels</Label>
                  <Input type="number" min="1" max="20" value={inhouse.levels}
                         onChange={(e) => setInhouse((f) => ({ ...f, levels: e.target.value }))}
                         data-testid="request-levels-input" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Priority</Label>
                  <select value={inhouse.priority} onChange={(e) => setInhouse((f) => ({ ...f, priority: e.target.value }))}
                          data-testid="request-priority-select"
                          className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                    <option value="Routine">Routine</option><option value="STAT">STAT</option>
                  </select>
                </div>
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Reason / note</Label>
                  <DictateButton value={inhouse.note} onChange={(t) => setInhouse((f) => ({ ...f, note: t }))}
                                 field="request_note" caseId={selectedCase} testid="dictate-request-note" />
                </div>
                <Textarea value={inhouse.note} onChange={(e) => setInhouse((f) => ({ ...f, note: e.target.value }))}
                          placeholder="e.g. deeper levels to rule out invasion"
                          data-testid="request-note-input" className="mt-1 min-h-[70px] border-slate-300" />
              </div>
            </>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Test / consultation requested</Label>
                  <Input value={sendout.test_requested} onChange={(e) => setSendout((f) => ({ ...f, test_requested: e.target.value }))}
                         placeholder="e.g. BRAF V600E, expert GI consult"
                         data-testid="request-test-input" className="mt-1 h-11 border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Destination lab (optional)</Label>
                  <Input value={sendout.destination_lab} onChange={(e) => setSendout((f) => ({ ...f, destination_lab: e.target.value }))}
                         placeholder="Leave blank — histology will arrange"
                         data-testid="request-destination-input" className="mt-1 h-11 border-slate-300" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">
                  Material going out — tick blocks and/or slides ({sendout.specimen_ids.length} selected)
                </Label>
                <div className="mt-1 max-h-56 overflow-y-auto border border-slate-300" data-testid="request-material-picker">
                  {specimens.length === 0 ? (
                    <p className="px-3 py-4 text-sm text-slate-500">
                      {selectedCase ? "No blocks or slides logged on this case yet." : "Pick a case to list its material."}
                    </p>
                  ) : (
                    <>
                      {[["Blocks", blocks], ["Slides", slides]].map(([label, list]) => list.length > 0 && (
                        <div key={label}>
                          <p className="bg-slate-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</p>
                          {list.map((s) => (
                            <label key={s.id} data-testid={`material-option-${s.barcode}`}
                                   className="flex cursor-pointer items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm hover:bg-slate-50">
                              <input type="checkbox" checked={sendout.specimen_ids.includes(s.id)}
                                     onChange={() => toggleMaterial(s.id)} className="h-4 w-4 accent-[#134e4a]" />
                              <span className="font-mono text-slate-800">{s.barcode}</span>
                              <span className="text-slate-600">
                                {s.kind === "block" ? `Block ${s.block_designation || s.label}` : s.slide_type}
                              </span>
                              {s.checked_out && (
                                <span className="ml-auto bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">
                                  out: {s.checked_out_to}
                                </span>
                              )}
                            </label>
                          ))}
                        </div>
                      ))}
                    </>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Ticked material is checked out of Blocks &amp; Slides automatically once the courier has it, and
                  checked back in when it is returned.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Priority</Label>
                  <select value={sendout.priority} onChange={(e) => setSendout((f) => ({ ...f, priority: e.target.value }))}
                          data-testid="request-sendout-priority"
                          className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                    <option value="Routine">Routine</option><option value="STAT">STAT</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Reason</Label>
                  <Input value={sendout.reason} onChange={(e) => setSendout((f) => ({ ...f, reason: e.target.value }))}
                         placeholder="e.g. second opinion" data-testid="request-reason-input"
                         className="mt-1 h-11 border-slate-300" />
                </div>
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Note to histology</Label>
                  <DictateButton value={sendout.notes} onChange={(t) => setSendout((f) => ({ ...f, notes: t }))}
                                 field="sendout_note" caseId={selectedCase} testid="dictate-sendout-note" />
                </div>
                <Textarea value={sendout.notes} onChange={(e) => setSendout((f) => ({ ...f, notes: e.target.value }))}
                          placeholder="Special handling, who to call, deadline…"
                          data-testid="request-sendout-note" className="mt-1 min-h-[70px] border-slate-300" />
              </div>
            </>
          )}

          <Button type="submit" disabled={busy} data-testid="request-submit-button"
                  className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
            {busy ? "Sending…" : tab === "inhouse" ? "Send to histology" : "Request send-out"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
